import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '@/lib/api';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '@/components/ui';
import { CAREERS_SITE_FOOTER_COLUMNS, PUBLIC_FOOTER_COLUMNS } from '@/constants/publicPages';
import { isJobsSiteOrigin } from '@/lib/jobsSite';

const EMAIL_REGEX = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;

interface PublicFooterProps {
  trialEmail?: string;
  onTrialEmailChange?: (value: string) => void;
  onCtaClick?: () => void;
}

// Görünüm anasayfa (Roadmap v9) tasarımının footer'ından gelir: lacivert zemin, Geist, #8892B8 metin.
export const PublicFooter = ({ trialEmail = '', onTrialEmailChange, onCtaClick }: PublicFooterProps) => {
  const { t } = useTranslation();
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isNewsletterSubmitting, setIsNewsletterSubmitting] = useState(false);

  // On Peoply Kariyer's own domain the footer is the candidates': its own pages, no newsletter or
  // payment logos (those are Peoply's, for companies).
  const onJobsSite = isJobsSiteOrigin();
  const columns = onJobsSite ? CAREERS_SITE_FOOTER_COLUMNS : PUBLIC_FOOTER_COLUMNS;

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;
    if (!EMAIL_REGEX.test(newsletterEmail)) {
      toast.error(t('publicPages.footer.newsletterInvalidEmail'));
      return;
    }
    setIsNewsletterSubmitting(true);
    try {
      await api.post('/newsletter/subscribe', { email: newsletterEmail });
      toast.success(t('publicPages.footer.newsletterSuccess'));
      setNewsletterEmail('');
    } catch {
      toast.error(t('publicPages.footer.newsletterError'));
    } finally {
      setIsNewsletterSubmitting(false);
    }
  };

  return (
    <footer
      className='border-t border-footer-border bg-footer font-[Geist,ui-sans-serif,system-ui,sans-serif] text-footer-text'
      id='contact'
    >
      <div className='mx-auto max-w-[1280px] px-4 pb-10 pt-16 md:px-16'>
        <div className='grid gap-12 lg:grid-cols-[2fr_1fr_1fr_2fr]'>
          <div>
            <div className='flex items-center gap-[9px]'>
              <img src='/landing/peoply-mark.svg' alt='' className='h-[31px] w-[34px]' />
              <span className='text-[23px] font-semibold tracking-[-.025em] text-footer-heading'>Peoply</span>
            </div>
            <p className='mt-5 max-w-xs text-[13.5px] leading-[1.6]'>
              {t(onJobsSite ? 'publicPages.careers.footer.tagline' : 'publicPages.footer.tagline')}
            </p>

            {/* SSL badge */}
            <div className='mt-6 inline-flex items-center gap-2 rounded-[10px] border border-footer-border bg-footer-heading/5 px-3 py-2'>
              <svg
                className='h-4 w-4 shrink-0 text-footer-link-hover'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
                strokeWidth={2.5}
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M12 2C9 2 6.5 3.5 6.5 3.5S4 5 4 9v3c0 5.25 8 10 8 10s8-4.75 8-10V9c0-4-2.5-5.5-2.5-5.5S15 2 12 2z'
                />
                <path strokeLinecap='round' strokeLinejoin='round' d='M9 12l2 2 4-4' />
              </svg>
              <span className='text-xs font-medium text-footer-heading'>{t('publicPages.footer.sslBadge')}</span>
            </div>

            <div className='mt-6 flex flex-col gap-2'>
              <a
                href='mailto:info@peoply.ai'
                className='inline-flex items-center gap-2 text-sm transition-colors hover:text-footer-link-hover'
              >
                <svg
                  className='h-4 w-4 shrink-0'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                  strokeWidth={1.75}
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0l-9.75 6.75L2.25 6.75'
                  />
                </svg>
                info@peoply.ai
              </a>
              <a
                href='tel:+905302681872'
                className='inline-flex items-center gap-2 text-sm transition-colors hover:text-footer-link-hover'
              >
                <svg
                  className='h-4 w-4 shrink-0'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                  strokeWidth={1.75}
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z'
                  />
                </svg>
                +90 530 268 18 72
              </a>
            </div>

            <div className='mt-6 flex gap-3'>
              <a
                href='#'
                className='flex h-9 w-9 items-center justify-center rounded-full border border-footer-border text-footer-text transition hover:border-footer-link-hover hover:text-footer-link-hover'
              >
                <svg className='h-4 w-4' fill='currentColor' viewBox='0 0 24 24'>
                  <path d='M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.413c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.265h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z' />
                </svg>
              </a>
              <a
                href='#'
                className='flex h-9 w-9 items-center justify-center rounded-full border border-footer-border text-footer-text transition hover:border-footer-link-hover hover:text-footer-link-hover'
              >
                <svg className='h-4 w-4' fill='currentColor' viewBox='0 0 24 24'>
                  <path d='M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' />
                </svg>
              </a>
              <a
                href='#'
                className='flex h-9 w-9 items-center justify-center rounded-full border border-footer-border text-footer-text transition hover:border-footer-link-hover hover:text-footer-link-hover'
              >
                <svg className='h-4 w-4' fill='currentColor' viewBox='0 0 24 24'>
                  <path d='M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z' />
                </svg>
              </a>
              <a
                href='#'
                className='flex h-9 w-9 items-center justify-center rounded-full border border-footer-border text-footer-text transition hover:border-footer-link-hover hover:text-footer-link-hover'
              >
                <svg className='h-4 w-4' fill='currentColor' viewBox='0 0 24 24'>
                  <path d='M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z' />
                </svg>
              </a>
            </div>
          </div>

          {columns.map((col) => (
            <div key={col.titleKey}>
              <p className='mb-5 font-[Geist_Mono,ui-monospace,monospace] text-[11.5px] font-medium uppercase tracking-[.2em] text-footer-link-hover'>
                {t(col.titleKey)}
              </p>
              <ul className='space-y-3'>
                {col.links.map((link) => (
                  <li key={link.labelKey}>
                    {link.to ? (
                      <Link to={link.to} className='text-sm transition-colors hover:text-footer-link-hover'>
                        {t(link.labelKey)}
                      </Link>
                    ) : (
                      <a href={link.href} className='text-sm transition-colors hover:text-footer-link-hover'>
                        {t(link.labelKey)}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {!onJobsSite && (
            <div>
              <p className='mb-2 text-[15px] font-medium text-footer-heading'>
                {t('publicPages.footer.newsletterTitle')}
              </p>
              <p className='mb-4 text-[13px] leading-[1.6]'>{t('publicPages.footer.newsletterSubtitle')}</p>
              <form onSubmit={handleNewsletterSubmit} className='flex gap-2'>
                <Input
                  type='email'
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  placeholder={t('publicPages.footer.newsletterPlaceholder')}
                  required
                  className='h-11 flex-1 rounded-[10px] border-footer-border bg-footer-heading/5 text-footer-heading placeholder:text-footer-muted hover:border-footer-muted focus-visible:border-footer-link-hover focus-visible:ring-footer-link-hover/30'
                />
                <Button
                  type='submit'
                  disabled={isNewsletterSubmitting}
                  className='h-11 rounded-[10px] bg-footer-heading px-5 font-medium text-footer hover:bg-white active:bg-footer-heading'
                >
                  {isNewsletterSubmitting ? '...' : t('publicPages.footer.newsletterButton')}
                </Button>
              </form>
            </div>
          )}
        </div>

        {/* Payment logos */}
        {!onJobsSite && (
          <div className='mt-12 flex flex-wrap items-center gap-4 border-t border-footer-border pt-8'>
            <span className='text-[13px] text-footer-muted'>{t('publicPages.footer.securePayment')}</span>
            {/* iyzico logo band */}
            <img
              src='/footer_iyzico_ile_ode/White/logo_band_white.svg'
              alt='iyzico ile öde - Mastercard, Visa, American Express, Troy'
              className='h-5 w-auto opacity-80'
            />
          </div>
        )}

        <div className='mt-8 border-t border-footer-border pt-6'>
          <p className='text-[13px] text-footer-muted'>
            Copyright © 2026 <span className='text-footer-heading'>Peoply</span>. {t('publicPages.footer.copyright')}
          </p>
        </div>
      </div>
    </footer>
  );
};
